import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { IconBrandApple, IconBrandGoogle } from '@tabler/icons-react';
import { beginSignIn, type ProviderId } from '@/lib/auth';
import {
  Button,
  FlowBackdrop,
  Input,
  InputEmail,
  LanguageSelect,
  isEmail,
  cn,
  useLocale,
  useToast,
  PRESS,
} from 'escrowbuild-ui';

import { useT } from '@/i18n';
import { usePageTitle } from '@/app/usePageTitle';

/* =============================================================================
   Sign-in
   -----------------------------------------------------------------------------
   `/` is the entry point of the mockup, so this screen is the first thing a
   visitor meets. The approved composition is the Alliance fullscreen login pack
   (`Alliance_Login_Backgrounds_Fullscreen_v2_2026-09-24`), whose README states
   the behaviour implemented here:

     - "The revised viewer fills the viewport with the animated background and
       places the social sign-in controls above it. There is no separate sidebar
       or opaque login panel." The scene covers the stage: the artwork's own SVG
       declares `preserveAspectRatio="xMidYMid slice"` — cover, not meet — so it
       runs edge to edge and crops horizontally rather than letterboxing itself
       into a band. `FlowBackdrop` already paints `object-cover`; the stage only
       has to let it fill.
     - The sign-in controls are a foreground layer over that artwork.
     - The pack ships no wordmark and says why: "The logo has no added wordmark
       because the public brand name has not yet been supplied." It has been, so
       the name sits at the top of the stage, where the mark's geometry cannot
       collide with it.

   The three provider buttons LEAVE THE APP. Consent happens on Google's,
   Microsoft's or Apple's own origin, driven by the API, so a provider button is
   a top-level navigation and not a router push — the app is gone before the
   provider's screen arrives. `startSignIn` below is the single place that
   decides it, and `lib/auth.ts` is the single place that knows where it goes.
   ============================================================================= */

/**
 * The Microsoft mark, drawn here because nothing in the project carries it.
 *
 * `@tabler/icons-react` no longer ships brand marks — there is no
 * `IconBrandMicrosoft` in the installed version, and the design system's own
 * icon folder holds app/PWA icons rather than a UI icon set. So the choice is
 * between a mark drawn here and a provider button with no mark at all.
 *
 * It follows the treatment the other two already have: Google and Apple come
 * from Tabler as single-path monochrome glyphs painted with `currentColor`, so
 * this is the same construction in the same colour, not a different visual
 * language dropped into the middle of the card. It takes the button's text
 * colour rather than Microsoft's four-colour palette — the house rule forbids
 * raw colour values, and a full-colour mark would be the only coloured thing in
 * an otherwise monochrome control. The geometry is the mark's own: four squares
 * with a gutter.
 */
function MicrosoftMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="8.5" height="8.5" />
      <rect x="9.5" y="0" width="8.5" height="8.5" />
      <rect x="0" y="9.5" width="8.5" height="8.5" />
      <rect x="9.5" y="9.5" width="8.5" height="8.5" />
    </svg>
  );
}

/** CONVENTIONS §5 — mandatory on every interactive element. */
const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

/**
 * The pack's `.link-button`: a copper underlined text control.
 *
 * It is a `<button>`, not an `<a>`, because every one of these switches the
 * card's view — nothing navigates, so nothing may present itself as a link.
 * `w-full` reproduces the pack's `display:block`, which is what centres the
 * label on the card's own axis.
 */
const LINK_BUTTON = cn(
  'block w-full rounded-xs bg-transparent py-2.5 text-xs leading-normal text-brand',
  'underline underline-offset-[0.2em]',
  'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)] hover:text-primary-hover',
  PRESS,
  FOCUS_RING,
);

type AuthView = 'social' | 'email' | 'forgot';

/*
   The email rule is the FIELD's, not a second copy of it.

   `InputEmail` validates with its own pattern before it ever consults a caller's
   `validate`, so a rule written here would not be the one the field renders —
   it would only be a second verdict on the same string. The submit handler asks
   the same exported predicate instead, which is what makes "invalid" mean one
   thing on this screen.
*/

export function LoginPage() {
  const { toast } = useToast();
  const { locale, setLocale } = useLocale();
  const t = useT();

  // Names this page in the browser tab. It renders outside both shells, so
  // nothing else calls this for it — without it `/` was the one route whose tab
  // said only the product name and not the page.
  usePageTitle();

  const [view, setView] = useState<AuthView>('social');

  // The provider button that was pressed. It holds the loading state while the
  // browser tears the app down for the consent screen, which on a slow
  // connection is the only feedback that the click registered.
  const [busy, setBusy] = useState<ProviderId | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  // One counter per form, bumped on a submit attempt to reveal every field's
  // error (CONVENTIONS §11). They are reset on every view change, because a
  // count that survived the switch would surface an error on a field this view
  // has never been submitted from.
  const [loginSubmits, setLoginSubmits] = useState(0);
  const [resetSubmits, setResetSubmits] = useState(0);

  // Bumped by each switch, never by anything else. The focus effect below is
  // keyed on it rather than on `view` so that it cannot fire on first paint and
  // steal focus from the document at load.
  const [focusRequest, setFocusRequest] = useState(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const resetEmailRef = useRef<HTMLInputElement>(null);
  const emailSwitchRef = useRef<HTMLButtonElement>(null);

  /**
   * The control that had focus is hidden by the switch, and focus on a hidden
   * element falls to `<body>` — which strands a keyboard user at the top of the
   * document with no announcement of what changed. So each switch moves focus to
   * the control the new view opens on.
   */
  useEffect(() => {
    if (focusRequest === 0) return;
    const target =
      view === 'email' ? emailRef : view === 'forgot' ? resetEmailRef : emailSwitchRef;
    target.current?.focus();
  }, [focusRequest, view]);

  function showView(next: AuthView) {
    // The password never survives a view change: the pack clears it too, and a
    // credential left in a form the visitor has navigated away from is a
    // credential still sitting in the page.
    setPassword('');
    setLoginSubmits(0);
    setResetSubmits(0);
    // Forgot password opens with the address already entered, so the visitor
    // does not retype what the previous view just collected.
    if (next === 'forgot') setResetEmail(email);
    setView(next);
    setFocusRequest((count) => count + 1);
  }

  function startSignIn(id: ProviderId) {
    // The one place a provider button leaves the app. `beginSignIn` mints the
    // nonce, keeps it for the callback to check, and assigns the consent URL —
    // so the destination, the nonce and the storage are decided in one function
    // rather than three that have to agree.
    setBusy(id);
    beginSignIn(id);
  }

  function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Resolved before anything else runs, so an invalid form is blocked rather
    // than navigated and then apologised for.
    const invalid: Array<RefObject<HTMLInputElement | null>> = [];
    if (email.trim() === '' || !isEmail(email)) invalid.push(emailRef);
    if (password === '') invalid.push(passwordRef);

    if (invalid.length > 0) {
      // Reveals the error on every field, including one never blurred, and
      // moves focus to the first invalid one — CONVENTIONS §12. The announcement
      // is the message row's own `role="alert"` (`forms/text/Field.tsx:73`).
      setLoginSubmits((count) => count + 1);
      invalid[0].current?.focus();
      return;
    }

    // The API signs people in through a provider only — the contract has no
    // password grant — so this form cannot sign anyone in and must not say it
    // did. The platform is behind a session now, so a "signed in" toast would
    // be followed by the sign-in screen it just left.
    toast({
      variant: 'info',
      title: t('login.notConnectedTitle'),
      description: t('login.notConnectedBody', { email }),
    });
  }

  function handleReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (resetEmail.trim() === '' || !isEmail(resetEmail)) {
      setResetSubmits((count) => count + 1);
      resetEmailRef.current?.focus();
      return;
    }

    // Nothing is sent and nothing is stored. Saying "sent" would be a claim the
    // build cannot support.
    toast({
      variant: 'info',
      title: t('login.resetNotSentTitle'),
      description: t('login.resetNotSentBody', { email: resetEmail }),
    });
  }

  return (
    <main
      className={cn(
        'relative isolate h-dvh min-h-[100svh] overflow-hidden bg-canvas',
        // The pack's own short-window rule: below 700px of height the stage
        // stops shrinking and scrolls, rather than pushing the card off-screen.
        '[@media(max-height:700px)]:h-auto [@media(max-height:700px)]:min-h-[700px] [@media(max-height:700px)]:overflow-auto',
      )}
    >
      {/* The pack's `.artwork` ground, from tokens so it flips with the theme. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_43%,var(--surface),var(--canvas)_65%)]"
      />

      {/*
        REQUIREMENT — the background must not restart when the view changes.

        This layer is a SIBLING of the card, never inside the subtree that swaps
        between social / email / forgot. A remount would re-request the scene and
        restart its 20-second loop from frame zero, which is precisely the effect
        the approved pack exists for.

        FULL BLEED, and the scene's own aspect ratio is what decides the fit — not
        a wrapper's. It was previously parked in a centred 16:10 box, so on a
        390x844 viewport the artwork rendered as a 390x244 band floating in the
        middle of the page with flat ground above and below, and its box edges
        showed as a faded seam. The client's requirement is the opposite: the
        scene runs to the top and the bottom limit, and horizontal overflow is
        acceptable. `FlowBackdrop` already paints its image `object-cover`, so
        removing the wrapper is what lets it fill — the artwork covers the
        viewport and crops left and right, and `overflow-hidden` on the stage
        means that crop never becomes a scrollbar.

        The crop is biased upward (`object-[50%_36%]`) because the mark is baked
        into the scene at its centre; raising the visible window lifts the mark
        clear of the card instead of letting the card cut through it.
      */}
      <FlowBackdrop
        tone="auto"
        className="[&>img]:object-[50%_36%]"
      />

      {/*
        A real skip link: the first focusable element in the document, off-screen
        until it holds focus, jumping to the card. The card carries
        `tabindex="-1"` so that the jump moves focus rather than only scrolling.
      */}
      <a
        href="#login-card"
        className={cn(
          // Parked off-screen exactly as the pack parks it, rather than hidden:
          // a display:none skip link is not focusable and so cannot be reached.
          'fixed left-5 -top-[70px] z-50 rounded-xs border border-line-strong bg-surface px-3 py-3 text-xs font-medium text-heading',
          'focus:top-3',
          FOCUS_RING,
        )}
      >
        {t('login.skip')}
      </a>

      <h1 className="dhd-sr-only">{t('login.heading')}</h1>

      {/*
        The language control, twice over: here and in the header of both shells.

        This screen renders OUTSIDE both of them, so there is no header to carry
        the selector — and a visitor whose browser guessed wrong has to be able
        to say so before they can read the form they are being asked to fill in.
        It sits centred directly BELOW the wordmark, in the same control shape
        the header uses, so it reads as the same control rather than as a second,
        unrelated one.

        Below and not beside, which is a measurement rather than a preference:
        the wordmark is a full-width centred line, and at 320px the two boxes
        overlapped — the flag sat under the name. Stacked, the pair cannot
        collide at any width, and the strip they sit in is ribbon and ground at
        every aspect ratio, so the stack stays clear of the mark and the card.
      */}
      <div className="absolute inset-x-0 top-[max(5rem,12svh)] z-20 flex justify-center">
        <LanguageSelect locale={locale} onLocaleChange={setLocale} />
      </div>

      {/*
        The wordmark.

        The pack ships NO wordmark, and says why in its own README — "The logo
        has no added wordmark because the public brand name has not yet been
        supplied." It has been supplied since, so the name goes where the design
        left room for it.

        It sits at the TOP of the stage rather than directly above the card. The
        scene paints its mark at the centre at a size that fills the height once
        the artwork covers a tall viewport, so anything placed just above the
        card lands on the mark's geometry — measured at 390x844, the name
        overlapped the mark and the card cut through its lower third. The top of
        the stage is ribbon and ground at every aspect ratio, which makes it the
        one position where the name cannot collide with the mark or the card.

        `pointer-events-none`, because the line's BOX spans the whole stage even
        though its text does not: as a plain `<p>` it swallowed every press in
        the strip it sits in, which is where the language control now lives.
      */}
      <p className="pointer-events-none absolute inset-x-0 top-[max(1.75rem,5svh)] z-20 text-center font-display text-xl font-semibold tracking-brand text-heading uppercase min-[700px]:text-2xl">
        escrowbuild.io
      </p>

      <section
        id="login-card"
        tabIndex={-1}
        aria-label={t('login.heading')}
        className={cn(
          // Lower on desktop, where the motion controls sit in the bottom-right
          // corner rather than across the bottom — that extra room lets the card
          // clear the lower third of the mark instead of cutting across it.
          'absolute bottom-[max(5.5rem,11svh)] left-1/2 z-20 -translate-x-1/2',
          'min-[700px]:bottom-10',
          'w-[min(336px,calc(100vw-40px))]',
          'rounded-xs border border-line-strong bg-surface p-4 shadow-hard',
          'min-[700px]:p-5',
        )}
      >
        {/* The card's copper tab: a short accent bar overhanging the top edge. */}
        <span
          aria-hidden="true"
          className="absolute -top-1 left-5 h-1.5 w-12 skew-x-[-11deg] bg-accent"
        />

        <div
          id="login-card-social"
          className={cn('flex flex-col gap-3', view !== 'social' && 'hidden')}
        >
          <Button
            variant="outline"
            size="lg"
            block
            leadingIcon={<IconBrandGoogle size={18} aria-hidden="true" />}
            loading={busy === 'google-oauth2'}
            onClick={() => startSignIn('google-oauth2')}
          >
            {t('login.continueGoogle')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            block
            leadingIcon={<IconBrandApple size={18} aria-hidden="true" />}
            loading={busy === 'apple-id'}
            onClick={() => startSignIn('apple-id')}
          >
            {t('login.continueApple')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            block
            leadingIcon={<MicrosoftMark />}
            loading={busy === 'microsoft-graph'}
            onClick={() => startSignIn('microsoft-graph')}
          >
            {t('login.continueMicrosoft')}
          </Button>

          <button
            ref={emailSwitchRef}
            type="button"
            aria-controls="login-card-email"
            aria-expanded={view === 'email'}
            onClick={() => showView('email')}
            className={cn(LINK_BUTTON, 'mt-2 min-[700px]:mt-3')}
          >
            {t('login.emailSwitch')}
          </button>
        </div>

        <div id="login-card-email" className={cn('flex flex-col', view !== 'email' && 'hidden')}>
          {/*
            `noValidate`: the native constraint bubble is a second, differently
            worded validation layer, and the library's own field UI is the one
            that announces itself and takes focus. Errors stay hidden until a
            blur or this form's own submit count reveals them — a pristine field
            shows nothing (CONVENTIONS §11).
          */}
          <form noValidate onSubmit={handleLogin} className="flex flex-col gap-4">
            <InputEmail
              ref={emailRef}
              id="login-email"
              label={t('login.email')}
              value={email}
              onChange={setEmail}
              required
              submitCount={loginSubmits}
              size="lg"
              placeholder={t('login.emailPlaceholder')}
              autoComplete="username"
            />

            <Input
              ref={passwordRef}
              id="login-password"
              label={t('login.password')}
              type="password"
              value={password}
              onChange={setPassword}
              required
              submitCount={loginSubmits}
              size="lg"
              autoComplete="current-password"
            />

            <button
              type="button"
              onClick={() => showView('forgot')}
              className={cn(LINK_BUTTON, 'self-end text-2xs', '-my-2 w-auto')}
            >
              {t('login.forgot')}
            </button>

            <Button type="submit" variant="primary" size="lg" block>
              {t('login.submit')}
            </Button>
          </form>

          <button
            type="button"
            aria-controls="login-card-social"
            onClick={() => showView('social')}
            className={cn(LINK_BUTTON, 'mt-3')}
          >
            {t('login.backToSocial')}
          </button>
        </div>

        <div id="login-card-reset" className={cn('flex flex-col', view !== 'forgot' && 'hidden')}>
          <h2 className="mb-4 font-display text-xl font-semibold uppercase leading-snug tracking-heading text-heading">
            {t('login.resetHeading')}
          </h2>

          <form noValidate onSubmit={handleReset} className="flex flex-col gap-4">
            <InputEmail
              ref={resetEmailRef}
              id="login-reset-email"
              label={t('login.email')}
              value={resetEmail}
              onChange={setResetEmail}
              required
              submitCount={resetSubmits}
              size="lg"
              placeholder={t('login.emailPlaceholder')}
              autoComplete="email"
            />

            <Button type="submit" variant="primary" size="lg" block>
              {t('login.sendResetLink')}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => showView('email')}
            className={cn(LINK_BUTTON, 'mt-3')}
          >
            {t('login.backToLogin')}
          </button>
        </div>
      </section>
    </main>
  );
}
